import { Action } from 'redux';
import { buffers } from 'redux-saga';
import { actionChannel, call, fork, put, select, take } from 'redux-saga/effects';
import { UpdateMetricsDataAction, MetricsDataActionTypes } from '../actions/ActionTypes';
import { getMetricsDataPoints, notLoadedData } from '../selectors/selectMetrics';
import { metricsDataRequestStart } from '../actions/MetricsDataRequestStartAction';
import { metricsDataRequestSucceeded } from '../actions/MetricsDataRequestSucceededAction';
import { metricsDataRequestFailed } from '../actions/MetricsDataRequestFailedAction';
import { MetricDataList } from '../models/IMetricDataList';
import { MetricDataPointsChannel } from '@tableau/search-microview-communication-channels';
import { SagaIterator } from 'redux-saga';

const STARTING_DATA_BUFFER_SIZE = 20;

/* TBD this is for the autorefresh which is not in prod
// The default timeout used between polls for refreshing the metrics if the server session information is not yet available
const DEFAULT_POLLING_DELAY_MS = 5000;
// The mimimum timeout used between polls that can be set by the server
const MINIMUM_POLLING_DELAY_MS = 1000;
*/

// In App, you can dispatch an action whenever you want to display metric data
// There is one `AppState` level object responsible for metric data points, `MetricsDataState`.
// Blocking:
// - Receive request for Metric Data
// - Filter requested ids to only those that are in `NOT_LOADED` state
// - Send Action to update filtered ids to `LOADING` state
// Non-Blocking:
// - Make network request for filtered ids
// - Receive results of network request
// - Send action to update successful ids to `LOADED` with returned data
// - Send action to update unsuccessful ids to `FAILED` with returned error info
export function* metricsDataSaga(): SagaIterator {
  // TBD yield fork(timerForMetricRefresh);
  const buffer = buffers.expanding<Action>(STARTING_DATA_BUFFER_SIZE)
  const requestChannel = yield actionChannel(MetricsDataActionTypes.UPDATE_DATA, buffer);
  while (true) {
    const action = yield take(requestChannel);
    yield call(startMetricDataUpdate, action);
  }
}

/* TBD this is for the autorefresh which is not in prod
export function* timerForMetricRefresh(): SagaIterator {
  while (true) {
    if (FeatureFlags.metricsAutoUpdateEnabled && !document.hidden) {
      yield call(startMetricDataAutoRefresh);
    }
    const state = yield select();
    const pollingDelay = getPollingDelay(state);
    yield call(delay, pollingDelay);
  }
}

export function getPollingDelay(state: AppState) {
  const pollingDelayFromServerMS = SessionServerSelectors.metricAutoUpdateTimeDelayMS(state);
  if (pollingDelayFromServerMS) {
    return Math.max(MINIMUM_POLLING_DELAY_MS, pollingDelayFromServerMS);
  } else {
    return DEFAULT_POLLING_DELAY_MS;
  }
}

const requestHeadersForAutoRefresh: RequestHeaders = {
  'X-Tableau-Feature': 'auto-refresh-metrics',
};

export function* startMetricDataAutoRefresh(): SagaIterator {
  try {
    const state = yield select(getMetricsDataState);
    //only get metrics from the refresh list that are not already loading
    const refreshList = getMetricRefreshList(state);
    if (refreshList.length === 0) {
      return;
    }
    yield put(metricsDataRequestStart(refreshList));
    yield fork(requestDataPointsInBatches, refreshList, requestHeadersForAutoRefresh);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}
*/

export function* startMetricDataUpdate(action: UpdateMetricsDataAction): SagaIterator {
  if (action.metrics.length === 0) {
    return;
  }
  const state = yield select(getMetricsDataPoints);
  const notLoadedIds = notLoadedData(state, action.metrics);
  if (notLoadedIds.length === 0) {
    return;
  }

  yield put(metricsDataRequestStart(notLoadedIds));

  yield fork(requestDataPointsInBatches, notLoadedIds);
}

const METRICS_PAGE_SIZE = 20;

export function* requestDataPointsInBatches(ids: ReadonlyArray<string>): SagaIterator {
  for (let i = 0; i < ids.length; i += METRICS_PAGE_SIZE) {
    const response = yield call(requestMetricData, ids.slice(i, i + METRICS_PAGE_SIZE));
    if (response.data && !isEmpty(response.data)) {
      yield put(metricsDataRequestSucceeded(response.data));
    }
    if (response.failures && !isEmpty(response.failures)) {
      yield put(metricsDataRequestFailed(response.failures));
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isEmpty = (obj: any) => !Object.keys(obj).length || !Object.keys(obj[Object.keys(obj)[0]]).length;

export async function requestMetricData(ids: string[]): Promise<MetricDataList> {
  return new Promise<MetricDataList>((resolve, reject) => {
    const metricDatapointsChannel = new MetricDataPointsChannel();
    metricDatapointsChannel
      .call({ metricIds: ids })
      .data?.then((result) => {
        resolve(result);
      })
      .catch((error) => reject(error));
  });
}
